import { useState, useRef, useEffect } from 'react'
import Button from './Button'

type Mode = 'ask' | 'add-info' | 'note'

type Message = {
  role: 'user' | 'assistant' | 'system'
  text: string
}

type Props = {
  onAsk: (text: string) => Promise<string>
  onAddInfo: (text: string) => Promise<void>
  onNote: (text: string) => Promise<void>
  placeholders?: { ask?: string; addInfo?: string; note?: string }
  emptyHints?: { ask?: string; addInfo?: string; note?: string }
}

const MODE_LABELS: Record<Mode, string> = {
  'ask': 'Ask',
  'add-info': 'Add info',
  'note': 'Note',
}

export default function ChatPanel({ onAsk, onAddInfo, onNote, placeholders = {}, emptyHints = {} }: Props) {
  const [mode, setMode] = useState<Mode>('ask')
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = async () => {
    const text = input.trim()
    if (!text || loading) return
    setInput('')
    setError(null)
    setMessages((prev) => [...prev, { role: 'user', text }])
    setLoading(true)

    try {
      if (mode === 'ask') {
        const reply = await onAsk(text)
        setMessages((prev) => [...prev, { role: 'assistant', text: reply }])
      } else if (mode === 'add-info') {
        await onAddInfo(text)
        setMessages((prev) => [...prev, { role: 'system', text: 'Profile updated with new information.' }])
      } else {
        await onNote(text)
        setMessages((prev) => [...prev, { role: 'system', text: 'Private note saved.' }])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
      setMessages((prev) => prev.slice(0, -1))
      setInput(text)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const placeholder = mode === 'ask'
    ? (placeholders.ask ?? 'Ask a question...')
    : mode === 'add-info'
    ? (placeholders.addInfo ?? 'Add new information...')
    : (placeholders.note ?? 'Leave a private note...')

  const emptyHint = mode === 'ask'
    ? (emptyHints.ask ?? 'Ask a question.')
    : mode === 'add-info'
    ? (emptyHints.addInfo ?? 'Paste or type new information to merge in.')
    : (emptyHints.note ?? 'Leave a private note stored in frontmatter.')

  return (
    <div className="bg-surface-low border border-border-subtle rounded-lg flex flex-col" style={{ height: '420px' }}>
      <div className="flex border-b border-border-subtle">
        {(Object.keys(MODE_LABELS) as Mode[]).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`flex-1 text-xs font-medium py-2.5 px-3 transition-colors rounded-t-lg ${
              mode === m
                ? 'text-primary border-b-2 border-primary bg-background'
                : 'text-text-muted hover:text-foreground'
            }`}
          >
            {MODE_LABELS[m]}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && (
          <p className="text-xs text-text-faint text-center mt-8">{emptyHint}</p>
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`text-sm ${
              msg.role === 'user' ? 'text-right' : msg.role === 'system' ? 'text-center' : 'text-left'
            }`}
          >
            <span
              className={`inline-block px-3 py-2 max-w-[85%] text-left whitespace-pre-wrap rounded-lg ${
                msg.role === 'user'
                  ? 'bg-primary text-white'
                  : msg.role === 'system'
                  ? 'text-xs text-primary font-medium'
                  : 'bg-surface-lowest text-foreground border border-border-subtle'
              }`}
            >
              {msg.text}
            </span>
          </div>
        ))}
        {loading && (
          <div className="text-left">
            <span className="inline-block px-3 py-2 bg-surface-lowest text-text-faint text-xs rounded-lg border border-border-subtle">
              {mode === 'ask' ? 'Thinking...' : 'Saving...'}
            </span>
          </div>
        )}
        {error && <p className="text-xs text-accent-error text-center">{error}</p>}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-border-subtle p-3 flex gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={2}
          disabled={loading}
          className="input-base resize-none placeholder:text-text-faint disabled:opacity-50"
        />
        <Button
          variant="primary"
          onClick={handleSend}
          disabled={!input.trim() || loading}
          className="self-end"
        >
          {loading ? '...' : '→'}
        </Button>
      </div>
    </div>
  )
}
